import React, { useEffect, useMemo, useState, useRef } from "react";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import {
  FaFilter,
  FaChevronDown,
  FaDownload,
  FaPlus,
  FaEdit,
  FaCog,
} from "react-icons/fa";
import {
  getData,
  postData,
  putData,
} from "../../services/FetchNodeservices";

/* ================= MODAL ================= */
function Modal({ open, title, children, onClose }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-xl w-full max-w-4xl shadow-xl">
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="font-semibold text-lg">{title}</h3>
          <button onClick={onClose} className="text-xl">✕</button>
        </div>
        <div className="p-4 max-h-[80vh] overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

/* ================= CHECKBOX FILTER ================= */
function CheckboxDropdown({ label, items, selected, setSelected, open, setOpen }) {
  return (
    <div className="relative">
      <button
        onClick={() => setOpen(open === label ? null : label)}
        className="h-10 px-4 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition duration-200 cursor-pointer flex justify-between items-center gap-2"
      >
        <FaFilter className="text-gray-400" />
        <span>{label} ({selected.length})</span>
        <FaChevronDown className="text-gray-400" />
      </button>

      {open === label && (
        <div className="absolute z-50 bg-white border border-gray-100 rounded-2xl mt-1.5 max-h-64 overflow-y-auto w-56 shadow-xl p-3 animate-fadeIn">
          {items.map((item) => (
            <label
              key={item}
              className="flex items-center gap-2 text-xs py-1.5 cursor-pointer hover:bg-gray-50 items-center text-gray-750"
            >
              <input
                type="checkbox"
                checked={selected.includes(item)}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                onChange={() =>
                  setSelected((prev) =>
                    prev.includes(item)
                      ? prev.filter((x) => x !== item)
                      : [...prev, item]
                  )
                }
              />
              {item}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

/* ================= MAIN ================= */
export default function UserClosureReport() {
  const [data, setData] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [editRow, setEditRow] = useState(null);

  /* DROPDOWN DATA */
  const [companies, setCompanies] = useState([]);
  const [teamLeads, setTeamLeads] = useState([]);
  const [associates, setAssociates] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [verticals, setVerticals] = useState([]);

  /* FILTER STATES */
  const [filterMonth, setFilterMonth] = useState([]);
  const [filterCompany, setFilterCompany] = useState([]);
  const [filterLead, setFilterLead] = useState([]);
  const [filterAssociate, setFilterAssociate] = useState([]);
  const [filterVertical, setFilterVertical] = useState([]);
  const [openFilter, setOpenFilter] = useState(null);

  const [columns, setColumns] = useState({
    Month: true,
    Company: true,
    "Team Lead": true,
    Associate: true,
    Candidate: true,
    Contact: true,
    Designation: true,
    Location: true,
    DOJ: true,
    CTC: true,
    Pct: true,
    Amount: true,
    Bill: true,
    Payment: true,
    "Joining Month": true,
    Vertical: true,
  });
  const [openColumns, setOpenColumns] = useState(false);
  const columnRef = useRef();

  useEffect(() => {
    const handler = (e) => {
      if (columnRef.current && !columnRef.current.contains(e.target)) {
        setOpenColumns(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* FORM */
  const emptyForm = {
    month: "",
    company: "",
    team_lead: "",
    associate_name: [],
    candidate_id: "",
    candidate_name: "",
    contact_no: "",
    designation: "",
    location: "",
    doj: "",
    status_jo: "",
    ctc_offered: "",
    pct: "",
    amount: "",
    bill_status: "",
    payment_status: "",
    joining_month: "",
    vertical: "",
  };
  const [form, setForm] = useState({ ...emptyForm });

  /* ================= FETCH ================= */
  const fetchAll = async () => {
    const userData = JSON.parse(localStorage.getItem("USER")) || {};
    const user_id = userData.id;

    const [closure, cmp, leads, joined, verts] = await Promise.all([
      getData("clouserreport/fetch/" + user_id),
      getData("clouserreport/companies"),
      getData("clouserreport/team-leads"),
      getData("clouserreport/joined-candidates"),
      getData("clouserreport/verticals"),
    ]);

    setData(closure?.data || []);
    setCompanies(cmp?.data || []);
    setTeamLeads(leads?.data || []);
    setCandidates(joined?.data || []);
    setVerticals(verts?.data || []);
  };

  useEffect(() => {
    fetchAll();
  }, []);

  /* ================= ASSOCIATES BY LEAD ================= */
  const fetchAssociates = async (leadId) => {
    const res = await getData(`clouserreport/team-members/${leadId}`);
    setAssociates(res?.data || []);
  };

  /* ================= AUTO FILL CANDIDATE ================= */
  const onCandidateSelect = (id) => {
    const c = candidates.find((x) => x.candidate_id === Number(id));
    if (!c) return;

    setForm((f) => ({
      ...f,
      candidate_id: id,
      candidate_name: c.candidate_name,
      contact_no: c.phone_number,
      designation: c.designation,
      location: `${c.city || ""}, ${c.state || ""}`,
      status_jo: c.status,
    }));
  };

  /* ================= AUTO CALC ================= */
  useEffect(() => {
    if (form.doj) {
      const m = dayjs(form.doj).format("MMMM");
      setForm((f) => ({ ...f, month: m, joining_month: m }));
    }
  }, [form.doj]);

  useEffect(() => {
    const ctc = Number(form.ctc_offered);
    const pct = Number(form.pct);
    if (!isNaN(ctc) && !isNaN(pct)) {
      setForm((f) => ({ ...f, amount: ((ctc * pct) / 100).toFixed(2) }));
    }
  }, [form.ctc_offered, form.pct]);

  /* ================= SAVE ================= */
  const saveForm = async () => {
    const payload = {
      ...form,
      associate_name: form.associate_name.join(", "),
      user_id: localStorage.getItem("user_id") || 59,
    };

    if (editRow)
      await putData(`clouserreport/update/${editRow.id}`, payload);
    else await postData("clouserreport/add", payload);

    setOpenForm(false);
    setEditRow(null);
    setForm({ ...emptyForm });
    fetchAll();
  };

  /* ================= FILTERED DATA ================= */
  const filteredData = useMemo(() => {
    return data.filter((r) => {
      if (filterMonth.length && !filterMonth.includes(r.month)) return false;
      if (filterCompany.length && !filterCompany.includes(r.company)) return false;
      if (filterLead.length && !filterLead.includes(r.team_lead)) return false;
      if (
        filterAssociate.length &&
        !filterAssociate.some((a) => r.associate_name?.includes(a))
      )
        return false;
      if (filterVertical.length && !filterVertical.includes(r.vertical))
        return false;
      return true;
    });
  }, [data, filterMonth, filterCompany, filterLead, filterAssociate, filterVertical]);

  /* ================= EXPORT ================= */
  const exportExcel = () => {
    const fieldMap = {
      Month: "month",
      Company: "company",
      "Team Lead": "team_lead",
      Associate: "associate_name",
      Candidate: "candidate_name",
      Contact: "contact_no",
      Designation: "designation",
      Location: "location",
      DOJ: "doj",
      CTC: "ctc_offered",
      Pct: "pct",
      Amount: "amount",
      Bill: "bill_status",
      Payment: "payment_status",
      "Joining Month": "joining_month",
      Vertical: "vertical",
    };

    const exportRows = filteredData.map((r) => {
      const obj = {};
      Object.keys(columns).forEach((colLabel) => {
        if (columns[colLabel]) {
          const dbKey = fieldMap[colLabel];
          obj[colLabel] = dbKey ? r[dbKey] || "-" : "-";
        }
      });
      return obj;
    });

    const ws = XLSX.utils.json_to_sheet(exportRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Closure Report");
    XLSX.writeFile(wb, "Closure_Report.xlsx");
  };

  /* ================= UI ================= */
  return (
    <div className="w-full space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        {/* TITLE */}
        <div>
          <h2 className="text-base font-extrabold text-gray-800 uppercase tracking-wider">
            Closure Report
          </h2>
          <div className="mt-1 flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider">
            <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-gray-600">
              Total: {filteredData.length}
            </span>
          </div>
        </div>

        {/* ACTION BUTTONS */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setOpenForm(true)}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl shadow-lg shadow-indigo-100 transition duration-200 active:scale-[0.99] flex items-center gap-2"
          >
            <FaPlus /> Add Closure
          </button>

          <button
            onClick={exportExcel}
            className="px-4 py-2 bg-white border border-gray-200 text-xs font-bold text-gray-700 rounded-xl shadow-sm hover:bg-gray-50 transition duration-200 active:scale-[0.99] flex items-center gap-2"
          >
            <FaDownload /> Export Excel 📄
          </button>

          {/* COLUMNS SELECTION BUTTON */}
          <div className="relative" ref={columnRef}>
            <button
              onClick={() => setOpenColumns((p) => !p)}
              className="px-4 py-2 bg-white border border-gray-200 text-xs font-bold text-gray-700 rounded-xl shadow-sm hover:bg-gray-50 transition duration-200 active:scale-[0.99] flex items-center gap-2"
            >
              <FaCog /> Columns
            </button>

            {openColumns && (
              <div className="absolute right-0 mt-2 bg-white p-4 border border-gray-100 rounded-2xl shadow-xl w-60 z-50 max-h-72 overflow-y-auto animate-fadeIn">
                {Object.keys(columns).map((c) => (
                  <label key={c} className="flex gap-2 text-xs py-1.5 cursor-pointer hover:bg-gray-50 items-center font-semibold text-gray-700">
                    <input
                      type="checkbox"
                      checked={columns[c]}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      onChange={() =>
                        setColumns((prev) => ({ ...prev, [c]: !prev[c] }))
                      }
                    />
                    {c}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* FILTER BAR */}
      <div className="border border-gray-100 rounded-2xl overflow-visible bg-white shadow-sm min-h-[480px]">
        <div className="border-b border-gray-100 bg-gray-50/20 p-4 relative z-30">
          <div className="flex flex-wrap gap-3 items-center">
            <CheckboxDropdown
              label="Month"
              items={[...new Set(data.map(d => d.month).filter(Boolean))]}
              selected={filterMonth}
              setSelected={setFilterMonth}
              open={openFilter}
              setOpen={setOpenFilter}
            />

            <CheckboxDropdown
              label="Company"
              items={[...new Set(data.map(d => d.company).filter(Boolean))]}
              selected={filterCompany}
              setSelected={setFilterCompany}
              open={openFilter}
              setOpen={setOpenFilter}
            />

            <CheckboxDropdown
              label="Team Lead"
              items={[...new Set(data.map(d => d.team_lead).filter(Boolean))]}
              selected={filterLead}
              setSelected={setFilterLead}
              open={openFilter}
              setOpen={setOpenFilter}
            />

            <CheckboxDropdown
              label="Associate"
              items={[
                ...new Set(
                  data.flatMap(d => d.associate_name?.split(", ") || [])
                ),
              ]}
              selected={filterAssociate}
              setSelected={setFilterAssociate}
              open={openFilter}
              setOpen={setOpenFilter}
            />

            <CheckboxDropdown
              label="Vertical"
              items={[...new Set(data.map(d => d.vertical).filter(Boolean))]}
              selected={filterVertical}
              setSelected={setFilterVertical}
              open={openFilter}
              setOpen={setOpenFilter}
            />
          </div>
        </div>

        {/* TABLE */}
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead>
              <tr className="border-b border-gray-150 bg-gray-50/50 text-[10px] font-bold text-gray-500 uppercase tracking-wider text-left">
                {[
                  "Month","Company","Team Lead","Associate","Candidate",
                  "Contact","Designation","Location","DOJ",
                  "CTC","Pct","Amount","Bill","Payment",
                  "Joining Month","Vertical"
                ].filter(h => columns[h]).concat("Action").map((h) => (
                  <th key={h} className="px-4 py-3.5 whitespace-nowrap font-bold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredData.map((r) => (
                <tr key={r.id} className="hover:bg-indigo-50/10 transition duration-150 text-gray-700">
                  {columns.Month && <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-500">{r.month}</td>}
                  {columns.Company && <td className="px-4 py-3 whitespace-nowrap text-gray-900 font-bold">{r.company}</td>}
                  {columns["Team Lead"] && <td className="px-4 py-3 whitespace-nowrap">{r.team_lead}</td>}
                  {columns.Associate && (
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="px-2 py-0.5 bg-indigo-50 text-indigo-650 rounded-full font-bold text-[10px]">
                        {r.associate_name}
                      </span>
                    </td>
                  )}
                  {columns.Candidate && <td className="px-4 py-3 whitespace-nowrap font-bold text-gray-900">{r.candidate_name}</td>}
                  {columns.Contact && <td className="px-4 py-3 whitespace-nowrap">{r.contact_no}</td>}
                  {columns.Designation && <td className="px-4 py-3 whitespace-nowrap font-semibold text-gray-800">{r.designation}</td>}
                  {columns.Location && <td className="px-4 py-3 whitespace-nowrap">{r.location}</td>}
                  {columns.DOJ && <td className="px-4 py-3 whitespace-nowrap">{r.doj}</td>}
                  {columns.CTC && <td className="px-4 py-3 whitespace-nowrap">₹{r.ctc_offered}</td>}
                  {columns.Pct && <td className="px-4 py-3 whitespace-nowrap">{r.pct}%</td>}
                  {columns.Amount && <td className="px-4 py-3 whitespace-nowrap font-bold text-indigo-650">₹{r.amount}</td>}
                  {columns.Bill && <td className="px-4 py-3 whitespace-nowrap">{r.bill_status}</td>}
                  {columns.Payment && (
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                          r.payment_status === "Paid"
                            ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/10"
                            : "bg-blue-50 text-blue-700 ring-1 ring-blue-600/10"
                        }`}
                      >
                        {r.payment_status}
                      </span>
                    </td>
                  )}
                  {columns["Joining Month"] && <td className="px-4 py-3 whitespace-nowrap">{r.joining_month}</td>}
                  {columns.Vertical && <td className="px-4 py-3 whitespace-nowrap">{r.vertical}</td>}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <button
                      onClick={() => {
                        setEditRow(r);
                        const leadObj = teamLeads.find(t => t.name === r.team_lead || t.id === Number(r.team_lead));
                        if (leadObj) {
                          fetchAssociates(leadObj.id);
                        } else {
                          setAssociates([]);
                        }
                        const matchedCand = candidates.find(c => c.candidate_name === r.candidate_name);
                        const candId = matchedCand ? matchedCand.candidate_id : "";
                        setForm({
                          ...r,
                          team_lead: leadObj ? leadObj.name : r.team_lead,
                          candidate_id: candId,
                          associate_name: r.associate_name ? r.associate_name.split(", ") : []
                        });
                        setOpenForm(true);
                      }}
                      className="px-2.5 py-1 border border-gray-200 hover:bg-indigo-50 text-[11px] font-bold text-indigo-600 rounded-lg transition"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
              {!filteredData.length && (
                <tr>
                  <td colSpan={17} className="py-20 text-center">
                    <div className="flex flex-col items-center justify-center gap-2 max-w-sm mx-auto">
                      <div className="w-12 h-12 rounded-2xl bg-indigo-50/80 flex items-center justify-center text-indigo-500 text-xl mb-1">
                        💰
                      </div>
                      <p className="text-sm font-bold text-gray-700">No Closure Sheet Records</p>
                      <p className="text-xs text-gray-400 font-medium">No closures found for the chosen filters or month period.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ADD / EDIT FORM */}
<Modal
  open={openForm}
  title={editRow ? "Edit Closure" : "Add Closure"}
  onClose={() => setOpenForm(false)}
>
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

    {/* MONTH */}
    <div>
      <label className="text-sm font-medium">Month</label>
      <select
        className="w-full border rounded px-3 py-2"
        value={form.month}
        onChange={(e) => setForm({ ...form, month: e.target.value })}
      >
        <option value="">Select Month</option>
        {[
          "January","February","March","April","May","June",
          "July","August","September","October","November","December"
        ].map(m => <option key={m}>{m}</option>)}
      </select>
    </div>

    {/* COMPANY */}
    <div>
      <label className="text-sm font-medium">Company</label>
      <select
        className="w-full border rounded px-3 py-2"
        value={form.company}
        onChange={(e) => setForm({ ...form, company: e.target.value })}
      >
        <option value="">Select Company</option>
        {companies.map(c => <option key={c}>{c}</option>)}
      </select>
    </div>

    {/* TEAM LEAD */}
    <div>
      <label className="text-sm font-medium">Team Lead</label>
      <select
        className="w-full border rounded px-3 py-2"
        value={
          teamLeads.find(t => t.name === form.team_lead || t.id === Number(form.team_lead))?.id || ""
        }
        onChange={(e) => {
          const leadId = e.target.value;
          const selectedLead = teamLeads.find(t => t.id === Number(leadId));
          setForm({ ...form, team_lead: selectedLead ? selectedLead.name : "", associate_name: [] });
          leadId ? fetchAssociates(leadId) : setAssociates([]);
        }}
      >
        <option value="">Select Team Lead</option>
        {teamLeads.map(t => (
          <option key={t.id} value={t.id}>{t.name}</option>
        ))}
      </select>
    </div>

    {/* ASSOCIATES (CHECKBOX) */}
    <div>
      <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5 ml-1">Associates</label>
      <div className="border border-gray-150 rounded-xl p-2.5 h-32 overflow-y-auto bg-gray-50">
        {associates.map(a => {
          const checked = form.associate_name.includes(a.name);
          return (
            <label key={a.id} className="flex items-center gap-2 text-xs py-1 cursor-pointer">
              <input
                type="checkbox"
                checked={checked}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                onChange={() =>
                  setForm(f => ({
                    ...f,
                    associate_name: checked
                      ? f.associate_name.filter(x => x !== a.name)
                      : [...f.associate_name, a.name],
                  }))
                }
              />
              {a.name}
            </label>
          );
        })}
      </div>
    </div>

    {/* CANDIDATE */}
    <div>
      <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5 ml-1">Candidate (Joined)</label>
      <select
        className="w-full h-10 px-3 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-800 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition duration-200 cursor-pointer"
        value={form.candidate_id}
        onChange={(e) => onCandidateSelect(e.target.value)}
      >
        <option value="">Select Candidate</option>
        {candidates.map(c => (
          <option key={c.candidate_id} value={c.candidate_id}>
            {c.candidate_name}
          </option>
        ))}
      </select>
    </div>

    {/* AUTO FILLED */}
    <input className="h-10 px-3 bg-gray-50 border border-gray-150 rounded-xl text-xs font-semibold text-gray-450 outline-none" value={form.contact_no} readOnly placeholder="Contact No" />
    <input className="h-10 px-3 bg-gray-50 border border-gray-150 rounded-xl text-xs font-semibold text-gray-450 outline-none" value={form.designation} readOnly placeholder="Designation" />
    <input className="h-10 px-3 bg-gray-50 border border-gray-150 rounded-xl text-xs font-semibold text-gray-450 outline-none" value={form.location} readOnly placeholder="Location" />

    {/* DOJ */}
    <div>
      <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1.5 ml-1">Date of Joining</label>
      <input
        type="date"
        className="w-full h-10 px-3 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-800 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition duration-200"
        value={form.doj}
        onChange={(e) => setForm({ ...form, doj: e.target.value })}
      />
    </div>

    {/* CTC / PCT / AMOUNT */}
    <input
      className="h-10 px-3 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-850 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition duration-200"
      placeholder="CTC Offered"
      value={form.ctc_offered}
      onChange={(e) => setForm({ ...form, ctc_offered: e.target.value })}
    />
    <input
      className="h-10 px-3 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-850 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition duration-200"
      placeholder="Percentage (%)"
      value={form.pct}
      onChange={(e) => setForm({ ...form, pct: e.target.value })}
    />
    <input
      className="h-10 px-3 bg-gray-50 border border-gray-150 rounded-xl text-xs font-semibold text-gray-455 outline-none"
      value={form.amount}
      readOnly
      placeholder="Amount"
    />

    {/* BILL STATUS */}
    <select
      className="h-10 px-3 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-800 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition duration-200 cursor-pointer"
      value={form.bill_status}
      onChange={(e) => setForm({ ...form, bill_status: e.target.value })}
    >
      <option value="">Bill Status</option>
      <option>Generated</option>
      <option>Not Generated</option>
    </select>

    {/* PAYMENT */}
    <select
      className="h-10 px-3 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-800 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition duration-200 cursor-pointer"
      value={form.payment_status}
      onChange={(e) => setForm({ ...form, payment_status: e.target.value })}
    >
      <option value="">Payment Status</option>
      <option>Paid</option>
      <option>Pending</option>
    </select>

    {/* JOINING MONTH */}
    <select
      className="h-10 px-3 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-800 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition duration-200 cursor-pointer"
      value={form.joining_month}
      onChange={(e) => setForm({ ...form, joining_month: e.target.value })}
    >
      <option value="">Joining Month</option>
      {[
        "January","February","March","April","May","June",
        "July","August","September","October","November","December"
      ].map(m => <option key={m}>{m}</option>)}
    </select>

    {/* VERTICAL (UNCHANGED) */}
    <select
      className="h-10 px-3 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-gray-800 focus:outline-none focus:ring-4 focus:ring-indigo-100/80 focus:border-indigo-500 transition duration-200 cursor-pointer"
      value={form.vertical}
      onChange={(e) => setForm({ ...form, vertical: e.target.value })}
    >
      <option value="">Vertical</option>
      {verticals.map(v => (
        <option key={v.id}>{v.dept_name}</option>
      ))}
    </select>
  </div>

  {/* ACTIONS */}
  <div className="mt-6 flex justify-end gap-3">
    <button
      onClick={() => setOpenForm(false)}
      className="px-4 py-2 border border-gray-200 text-xs font-bold text-gray-655 hover:bg-gray-50 rounded-xl transition duration-200 active:scale-[0.99]"
    >
      Cancel
    </button>
    <button
      onClick={saveForm}
      className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl shadow-lg shadow-indigo-150 transition duration-200 active:scale-[0.99]"
    >
      Save
    </button>
  </div>
</Modal>

    </div>
  );
}
